import React, { useState, useEffect } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

export default function CheckoutFormTester({ 
  uid, 
  customerEmail, 
  stripeCustomerId, 
  onPaymentMethodCreated,
  onPaymentComplete,
  isTestEnvironment = false
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [status, setStatus] = useState(null); // 'success', 'error', or null
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

  // Track changes in the payment element
  useEffect(() => {
    if (!elements) return;

    const paymentElement = elements.getElement('payment');
    if (paymentElement) {
      paymentElement.on('change', (event) => {
        if (event.value && event.value.type) {
          setSelectedPaymentMethod(event.value.type);
        }
      });
    }
  }, [elements]);

  // Auto-submit the form only in test environment mode
  useEffect(() => {
    if (isTestEnvironment && stripe && elements) {
      // Wait a short time to allow the form to initialize
      const timer = setTimeout(() => {
        handleSubmit({ preventDefault: () => {} });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [stripe, elements, isTestEnvironment]);

  const getFriendlyErrorMessage = (error) => {
    console.log('Error details:', error);
    
    // For Stripe Radar blocked payments (BIN rejection)
    if (error.payment_intent && error.payment_intent.last_payment_error) {
      const paymentError = error.payment_intent.last_payment_error;
      if (paymentError.code === 'card_declined' && paymentError.decline_code === 'insufficient_funds') {
        return 'The authorization hold did not work on this card. This indicates the card has insufficient funds or is not accepted for this service. Please use another card with sufficient funding.';
      }
    }
    
    // Check for blocked outcome from Radar
    if (error.payment_intent && error.payment_intent.charges && 
        error.payment_intent.charges.data && 
        error.payment_intent.charges.data.length > 0 && 
        error.payment_intent.charges.data[0].outcome && 
        error.payment_intent.charges.data[0].outcome.type === 'blocked') {
      return 'This card cannot be used for our service. Our fraud prevention system has blocked this payment. Please try a different card from another bank.';
    }
    
    // Default error message
    return error.message || 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !stripeCustomerId) {
      // Stripe.js hasn't loaded yet or customer ID not available
      return;
    }

    setIsLoading(true);

    try {
      // Confirm the payment/setup intent
      const { error: paymentError, paymentIntent, setupIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.origin + '/checkout-tester/success',
          payment_method_data: {
            billing_details: {
              email: customerEmail,
            }
          }
        },
      });

      if (paymentError) {
        console.log('Payment error details:', paymentError);
        setMessage(getFriendlyErrorMessage(paymentError));
        setStatus('error');
        setIsLoading(false);
        return;
      }

      // Check if we're in the testing flow
      if (onPaymentMethodCreated || onPaymentComplete) {
        if (setupIntent) {
          console.log('Setup intent confirmed:', setupIntent);
          // Call the callback to notify that a payment method was created
          if (onPaymentMethodCreated && setupIntent.payment_method) {
            onPaymentMethodCreated({ id: setupIntent.payment_method });
          }
        } else if (paymentIntent) {
          console.log('Payment intent confirmed:', paymentIntent);
          // Call the callback to notify that payment is complete
          if (onPaymentComplete && paymentIntent.payment_method) {
            onPaymentComplete({ id: paymentIntent.payment_method });
          }
        }
      } else {
        // Regular environment - create subscription with free trial
        const response = await fetch('/api/create-subscription', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            paymentMethodId: paymentIntent ? paymentIntent.payment_method : setupIntent.payment_method,
            priceId: 'price_1R7o5rK5A174OlbcwkSlu9cl', // Default price ID
            customerId: stripeCustomerId,
            email: customerEmail,
            uid: uid
          }),
        });

        const data = await response.json();

        if (data.error) {
          setMessage(getFriendlyErrorMessage(data));
          setStatus('error');
        } else {
          setMessage('Your subscription has been created successfully! Your 30-day free trial has started.');
          setStatus('success');
          
          // Redirect to overview after a short delay
          setTimeout(() => {
            window.location.href = 'https://relay.chatodyssey.com/overview?redirect-purchase';
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setMessage('An unexpected error occurred. Please try again.');
      setStatus('error');
    }

    setIsLoading(false);
  };

  const renderPaymentUI = () => {
    return (
      <button
        disabled={isLoading || !stripe || !elements || !stripeCustomerId}
        id="submit"
        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-lg transition duration-150 ease-in-out flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </>
        ) : (
          isTestEnvironment ? 'Auto-Processing...' : 'Start Trial'
        )}
      </button>
    );
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit} className="flex flex-col">
      <div className="mb-4">
        <h2 className="text-sm text-gray-400 mb-2">
          {isTestEnvironment 
            ? "TEST MODE: Automatically continuing test flow" 
            : "Relay Premium: 30 days free, renews at $4.99/month. Cancel Anytime."}
        </h2>
        
        <div className="bg-gray-700/70 backdrop-blur-md p-4 rounded-lg border border-white/10 shadow-lg" style={{ backdropFilter: 'blur(10px)' }}>
          <PaymentElement id="payment-element" options={{
            layout: {
              type: 'tabs',
              defaultCollapsed: false
            },
            paymentMethodOrder: ['card'],
            paymentMethodTypes: {
              card: {
                enabled: true
              },
              apple_pay: {
                enabled: false
              },
              google_pay: {
                enabled: false
              },
              cashapp: {
                enabled: false
              },
              link: {
                enabled: false
              }
            },
            fields: {
              billingDetails: 'auto'
            }
          }} />
        </div>
      </div>

      {message && (
        <div className={`p-4 mb-4 rounded-lg border ${status === 'success' 
          ? 'bg-green-800 text-green-100 border-green-600' 
          : 'bg-red-800 text-red-100 border-red-600'}`}>
          <p className="font-medium">{message}</p>
        </div>
      )}

      <div className="mt-2">
        {renderPaymentUI()}
        
        {!isTestEnvironment && (
          <p className="mt-3 text-sm text-gray-400 text-center">
            You can cancel anytime during your trial period.
          </p>
        )}
      </div>
    </form>
  );
} 